import { useState } from 'react';

type FilterType = 'All Projects' | 'Multifamily' | 'Senior Living & Healthcare';

const projects = [
  {
    name: 'Riverside Apartments',
    type: 'Multifamily Renovation',
    category: 'Multifamily',
    location: 'Richmond, VA',
    description: 'Exterior renovation of 120-unit apartment community including siding, windows, and railings',
    image: 'https://public.readdy.ai/ai/img_res/c96f68d6bac4711549fe23d66e83f76c.jpg',
  },
  {
    name: 'Heritage Memory Care',
    type: 'Senior Living Renovation',
    category: 'Senior Living & Healthcare',
    location: 'Atlanta, GA',
    description: 'Interior renovation of 60-bed memory care facility including lobby, corridors, and resident rooms',
    image: 'https://readdy.ai/api/search-image?query=modern%20senior%20living%20healthcare%20facility%20exterior%20renovation%20completed%20building%20professional%20architecture%20clean%20warm%20inviting%20design%20premium%20photography%20simple%20background&width=600&height=400&seq=proj02&orientation=landscape',
  },
  {
    name: 'Capitol Hill Commons',
    type: 'Multifamily Renovation',
    category: 'Multifamily',
    location: 'Washington, DC',
    description: 'Interior and exterior renovation of 85-unit mixed-use residential property',
    image: 'https://readdy.ai/api/search-image?query=mixed%20use%20urban%20apartment%20building%20Washington%20DC%20modern%20architecture%20residential%20commercial%20ground%20floor%20premium%20construction%20completed%20exterior%20professional%20photography&width=600&height=400&seq=proj03&orientation=landscape',
  },
  {
    name: 'Magnolia Senior Living',
    type: 'Senior Living Renovation',
    category: 'Senior Living & Healthcare',
    location: 'Nashville, TN',
    description: 'Tenant improvements and common area modernization of 45-unit assisted living community',
    image: 'https://readdy.ai/api/search-image?query=assisted%20living%20community%20exterior%20modern%20renovation%20completed%20building%20warm%20welcoming%20architecture%20landscaped%20grounds%20premium%20senior%20care%20facility%20professional%20photography&width=600&height=400&seq=proj04&orientation=landscape',
  },
  {
    name: 'Parkview Residences',
    type: 'Multifamily Renovation',
    category: 'Multifamily',
    location: 'Baltimore, MD',
    description: 'Full exterior envelope renovation including roofing, paint, and window replacement for 95-unit apartment building',
    image: 'https://readdy.ai/api/search-image?query=luxury%20apartment%20building%20exterior%20Baltimore%20Maryland%20modern%20residential%20architecture%20high%20end%20construction%20completed%20building%20professional%20photography%20clean%20background&width=600&height=400&seq=proj05&orientation=landscape',
  },
  {
    name: 'Sunrise Nursing Center',
    type: 'Senior Living Renovation',
    category: 'Senior Living & Healthcare',
    location: 'Charlottesville, VA',
    description: 'Phased interior renovation of 80-bed nursing home while facility remained occupied',
    image: 'https://readdy.ai/api/search-image?query=nursing%20home%20healthcare%20facility%20exterior%20renovation%20completed%20modern%20building%20professional%20architecture%20clean%20welcoming%20design%20premium%20quality%20photography%20simple%20background&width=600&height=400&seq=proj06&orientation=landscape',
  },
  {
    name: 'The Meridian',
    type: 'Multifamily Renovation',
    category: 'Multifamily',
    location: 'Alexandria, VA',
    description: 'Lobby, corridor, and amenity area renovation of 110-unit apartment complex',
    image: 'https://readdy.ai/api/search-image?query=transit%20oriented%20apartment%20complex%20Alexandria%20Virginia%20modern%20urban%20residential%20building%20exterior%20premium%20architecture%20completed%20construction%20professional%20photography%20clean%20background&width=600&height=400&seq=proj07&orientation=landscape',
  },
  {
    name: 'Harmony Gardens',
    type: 'Senior Living Renovation',
    category: 'Senior Living & Healthcare',
    location: 'Charlotte, NC',
    description: 'Storefront entrance replacement and interior fit-out for memory care wing addition',
    image: 'https://readdy.ai/api/search-image?query=memory%20care%20facility%20wing%20addition%20renovation%20exterior%20modern%20healthcare%20building%20completed%20construction%20warm%20inviting%20architecture%20professional%20photography%20landscaped%20grounds&width=600&height=400&seq=proj08&orientation=landscape',
  },
  {
    name: 'Commonwealth Apartments',
    type: 'Multifamily Renovation',
    category: 'Multifamily',
    location: 'Tysons, VA',
    description: 'Complete exterior renovation including siding, railings, and exterior amenity areas for 150-unit community',
    image: 'https://readdy.ai/api/search-image?query=class%20A%20luxury%20apartment%20community%20Tysons%20Virginia%20modern%20high%20end%20residential%20building%20exterior%20premium%20architecture%20completed%20construction%20professional%20photography%20clean%20sky%20background&width=600&height=400&seq=proj09&orientation=landscape',
  },
];

const filters: FilterType[] = ['All Projects', 'Multifamily', 'Senior Living & Healthcare'];

export default function ProjectGrid() {
  const [activeFilter, setActiveFilter] = useState<FilterType>('All Projects');

  const filtered = activeFilter === 'All Projects'
    ? projects
    : projects.filter((p) => p.category === activeFilter);

  return (
    <section className="bg-[#111111] py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-3 justify-center mb-14">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-7 py-3 rounded-md text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeFilter === filter
                  ? 'bg-[#C5A55A] text-[#1A1A1A]'
                  : 'bg-transparent border border-[#C5A55A] text-white hover:bg-[#C5A55A]/10'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Project Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((project) => (
            <div
              key={project.name}
              className="group bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5 hover:border-[#C5A55A]/60 transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Gold overlay on hover */}
                <div className="absolute inset-0 bg-[#C5A55A]/0 group-hover:bg-[#C5A55A]/10 transition-all duration-300" />
              </div>

              {/* Card Content */}
              <div className="p-6">
                {/* Type label */}
                <span className="text-[#C5A55A] text-xs font-bold uppercase tracking-widest mb-2 block">
                  {project.type}
                </span>
                {/* Project name */}
                <h3 className="text-white text-lg font-bold mb-2 group-hover:text-[#C5A55A] transition-colors duration-200">
                  {project.name}
                </h3>
                {/* Location */}
                <div className="flex items-center gap-1.5 text-white/50 text-sm mb-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-2-fill text-[#C5A55A] text-sm"></i>
                  </div>
                  <span>{project.location}</span>
                </div>
                {/* Description */}
                <p className="text-white/60 text-sm leading-relaxed border-t border-white/10 pt-4">
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
